import { useEffect, useState } from 'react'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Hinweisfenster, Nachfrage } from './Nachfrage'
import { satz, t } from './sprache'
import type { Kfrloeschlage } from './typen'

/**
 * **Rechtsklick → Position löschen … oder Summenzeile löschen …** (2.1
 * Abschnitt 3.6, R1705, R1707; 2.8 Abschnitt 13.41).
 *
 * Erst wird gefragt, dann gelöscht. **Was nicht gelöscht werden darf, sagt
 * ein Fenster mit dem Grund**: eine Position mit fester Aufgabe und eine
 * Position, unter der etwas hängt — Referenzen oder Positionen. Bis zum
 * 2026-10-14 gingen die Referenzen mit (R1334); seither werden sie erst
 * einzeln gelöscht. Der Menüpunkt steht deshalb an jeder Position — ein
 * fehlender Menüpunkt sagte nicht, warum.
 */
export function Kfrloeschfenster({
  zeile,
  titel,
  summenzeile = false,
  aufGeloescht,
  aufSchliessen,
}: {
  zeile: string
  /** Nummer und Name, wie sie in der Rechnung stehen. */
  titel: string
  /** Ob eine Summenzeile gelöscht wird und keine Position — das sagt die Überschrift. */
  summenzeile?: boolean
  aufGeloescht: () => void
  aufSchliessen: () => void
}) {
  const ueberschrift = t(summenzeile ? 'kfr.loeschen.titel.summenzeile' : 'kfr.loeschen.titel')
  const fehler = t('kfr.loeschen.fehler', { zeile: titel })
  const [lage, setLage] = useState<Kfrloeschlage | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  useEffect(() => {
    let gilt = true
    api
      .kfrLoeschlage(zeile)
      .then((l) => {
        if (gilt) setLage(l)
      })
      .catch((f: unknown) => {
        if (gilt) setMeldung(fachlicherSatz(f) ?? fehler)
      })
    return () => {
      gilt = false
    }
  }, [zeile, fehler])

  if (meldung) {
    return <Hinweisfenster titel={ueberschrift} meldung={meldung} aufSchliessen={aufSchliessen} />
  }

  if (!lage) {
    return null
  }

  if (lage.hindernis) {
    return (
      <Hinweisfenster
        titel={ueberschrift}
        meldung={satz(lage.hindernis)}
        aufSchliessen={aufSchliessen}
      />
    )
  }

  return (
    <Nachfrage
      titel={ueberschrift}
      frage={t('kfr.loeschen.frage', { zeile: titel })}
      hinweis={[
        t('kfr.loeschen.ohnereferenzen'),
        // **Unter der Position darüber hängt danach keine mehr** (2.8
        // Abschnitt 13.48, R1362) — gesagt, bevor es geschieht.
        lage.zeileWird ? t('kfr.loeschen.wirdzeile', { summe: lage.zeileWird }) : null,
      ]
        .filter(Boolean)
        .join(' ')}
      bestaetigen={t('kfr.loeschen.knopf')}
      gefahr
      aufJa={() => {
        api
          .kfrZeileLoeschen(zeile)
          .then(() => {
            aufGeloescht()
            aufSchliessen()
          })
          .catch((f: unknown) => setMeldung(fachlicherSatz(f) ?? fehler))
      }}
      aufSchliessen={aufSchliessen}
    />
  )
}
